import React from "react";
import ZoomMeetingEmbed from "./ZoomMeetingEmbed";
import ZoomMeetingEmbedMobile from "./ZoomMeetingEmbedMobile";
import ZohoMeetingEmbed from "./ZohoMeetingEmbed";
import GratsMeetEmbed from "./GratsMeetEmbed";
import ErrorFallback from "./ErrorFallback";

/**
 * MeetingProviderRouter
 *
 * Selects the right meeting embed component based on the `provider` field
 * in the join-config payload returned by the backend. Falls back to Zoom
 * for legacy responses that don't include a provider field.
 *
 * Backend join-config contract:
 *   - provider: 'zoom' | 'zoho' | 'grats'
 *   - mode:     'embedded' | 'iframe' | 'external_link'
 *   - meetingId, passcode, signature, sdkKey, userName, zoomMode, zakToken (Zoom)
 *   - joinUrl (Zoho only — never set for embedded Zoom or Grats Meet)
 *   - grant, embedBaseUrl (Grats Meet only — the grant is a credential our own
 *     server minted for this person; there is no link and no passcode)
 *
 * `view` selects which ZOOM surface to use, and only Zoom has two:
 *   - "client"    (default) Client View — takes over the page via #zmmtg-root.
 *                 The desktop classroom.
 *   - "component" Component View — renders into a div. The phone classroom
 *                 (/m/classroom/:batchId), and the view Zoom supports in
 *                 mobile browsers.
 * Grats embeds in-page either way, and Zoho always opens in a new tab (it
 * refuses to be framed), so both ignore it.
 *
 * `onRejoin` (resolves a fresh join config) and `endLabel` are Grats-only: a
 * dropped connection rejoins in place instead of reporting the meeting over.
 * `onStateChange` is Grats-only too, so a page's Grats behaviour never reaches
 * a Zoom or Zoho class.
 */
const MeetingProviderRouter = ({ joinConfig, onMeetingEnd, onRejoin, endLabel, leaveUrl, onStateChange, notifyOnBackgroundChat = false, autoPictureInPicture = false, view = "client" }) => {
  if (!joinConfig) {
    return <ErrorFallback message="Class configuration is unavailable." />;
  }

  const provider = joinConfig.provider || "zoom";

  if (provider === "grats") {
    return (
      <GratsMeetEmbed
        joinConfig={joinConfig}
        onMeetingEnd={onMeetingEnd}
        onRejoin={onRejoin}
        endLabel={endLabel}
        leaveUrl={leaveUrl}
        onStateChange={onStateChange}
      />
    );
  }

  if (provider === "zoho") {
    return (
      <ZohoMeetingEmbed
        joinConfig={joinConfig}
        onMeetingEnd={onMeetingEnd}
        leaveUrl={leaveUrl}
      />
    );
  }

  if (view === "component") {
    return <ZoomMeetingEmbedMobile zoomConfig={joinConfig} onMeetingEnd={onMeetingEnd} />;
  }

  // Default: Zoom Client View (preserves all legacy behaviour).
  return (
    <ZoomMeetingEmbed
      zoomConfig={joinConfig}
      onMeetingEnd={onMeetingEnd}
      leaveUrl={leaveUrl}
      notifyOnBackgroundChat={notifyOnBackgroundChat}
      autoPictureInPicture={autoPictureInPicture}
    />
  );
};

export default MeetingProviderRouter;
